import { DishCard } from 'components/cards/dish-card';
import { SeeAllCard } from 'components/cards/see-all-card';
import { CarouselRow } from 'components/carousels/carousel-row';
import { useCarousel } from 'components/carousels/CarouselController';
import { ScrollButtons } from 'components/carousels/ScrollButtons';
import { MaxWidthContainer } from 'components/city-blocks/max-width-container';
import { describeFilter } from 'components/dynamic-content/copy-util';
import { useRefState } from 'hooks/useRefState';
import Link from 'next/link';
import React, { useMemo } from 'react';
import { NoFollowLink } from 'shared/no-follow-link';
import { CityPageDishFragment, CityPageFilterFragment } from 'utils/gqlReactTypings.generated.d';

// NOTE: THESE MUST STAY IN SYNC
const PADDING = 16;

const SHADOW_BREATHING_ROOM = 36;
const IS_ONSCREEN_THRESHOLD = 32;
const utmParams = '&utm_content=dishes-see-all';

const isDish = (item): item is CityPageDishFragment => item.__typename === 'CityPageDish';

interface IProps {
  className?: string;
  dishes?: CityPageDishFragment[];
  positioningCopy?: string | null;
  seeAllHref: string;
  activeFilter?: CityPageFilterFragment;
}

export const DishesCarousel: React.FC<IProps> = ({ className, dishes, positioningCopy, seeAllHref, activeFilter }) => {
  const [ref, setRef] = useRefState<HTMLDivElement>();
  const items: CityPageDishFragment[] = useMemo(() => dishes?.filter(isDish) ?? [], [dishes]);
  const { scrollLeft, scrollRight, hasOverflow, numElementsLeft, numElementsRight } = useCarousel(
    ref,
    items,
    PADDING,
    IS_ONSCREEN_THRESHOLD
  );
  const filterDescription = activeFilter ? describeFilter(activeFilter).filterDescription : '';
  const header = `Popular ${filterDescription} Dishes`;

  if (!dishes?.length) return null;

  return (
    <MaxWidthContainer className={`my-4 ${className}`}>
      <div className='mb-6 flex flex-row justify-between px-4 md:px-0'>
        <div className=' flex flex-col'>
          <h3 className='m-0 text-2xl font-bold text-neutral-800 md:text-3xl'>{header}</h3>
          {positioningCopy && <span>{positioningCopy}</span>}
        </div>
        <Link
          href={`${seeAllHref}${utmParams}`}
          className='ml-16 hidden min-w-fit cursor-pointer text-base font-semibold md:flex'>
          See all
        </Link>
      </div>
      <div className={`relative w-full`}>
        <CarouselRow padding={PADDING} shadowBreathingRoom={SHADOW_BREATHING_ROOM} ref={setRef}>
          {dishes.map((dish) => (
            <DishCard className='shadow-[10px_16px_30px_rgba(0,0,0,0.1)]' key={dish.id} dish={dish} />
          ))}

          <NoFollowLink className='md:hidden' href={seeAllHref}>
            <SeeAllCard />
          </NoFollowLink>
        </CarouselRow>

        {hasOverflow && (
          <ScrollButtons
            className='absolute right-[-60px] top-0 hidden md:flex'
            elementName='dishes-carousel-scroll-buttons'
            scrollRight={scrollRight}
            scrollLeft={scrollLeft}
            numElementsLeft={numElementsLeft}
            numElementsRight={numElementsRight}
          />
        )}
      </div>
    </MaxWidthContainer>
  );
};
